import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { Box, BoxBackLink } from 'components/common/box';
import { ErrorPage } from 'components/error';
import { FiltersProvider } from 'components/common/filter';
import {
  CalendarEventFeedbackQuestionAnalytics,
  CalendarEventFeedbackQuestionAnswers
} from '.';

import type {
  GetCalendarEventFeedbackAnalyticsRes,
  GetCalendarEventFeedbackFormRes
} from 'shared-types/responses';

const filterId = 'calendar-event-feedback-question-answers';

const filters = {
  accountAccess: true,
  accountIdentifier: true,
  accountLogin: true,
  accountType: true,
  groups: true,
  supervisors: true,
  userAttributes: true
};

/**
 * Analytics for a single feedback question: the question's own metrics, and
 * a paginated, searchable, filterable list of the submissions that answered
 * it. Reached only by owners and superadmins - the feedback routes enforce
 * that server-side, so a direct link from anyone else 404s.
 */
const CalendarEventFeedbackQuestion = () => (
  <FiltersProvider filters={filters}>
    <CalendarEventFeedbackQuestionContent />
  </FiltersProvider>
);

const CalendarEventFeedbackQuestionContent = () => {
  const {
    messages,
    pageState: { calendarEvent }
  } = useReduxState();

  const router = useRouter();
  const questionId = router.query.questionId as string;

  const formUrl = `/admin/calendar-events/${calendarEvent.id}/feedback-form`;

  const { data: formData, loading: formLoading } =
    useApiData<GetCalendarEventFeedbackFormRes>({ url: formUrl });

  const { data: analyticsData, loading: analyticsLoading } =
    useApiData<GetCalendarEventFeedbackAnalyticsRes>({
      onlyIf: Boolean(questionId),
      url: `${formUrl}/analytics?questionId=${questionId}`
    });

  const question = formData?.form?.questions.find(
    (formQuestion) => String(formQuestion.id) === questionId
  );
  const analytics = question
    ? analyticsData?.analyticsByQuestionId[question.id]
    : undefined;

  if (!formLoading && !question) return <ErrorPage statusCode={404} />;

  return (
    <div className="question">
      <Box
        loading={formLoading || analyticsLoading}
        title={
          <BoxBackLink
            as={`/calendar/${calendarEvent.id}/feedback`}
            href="/calendar/[calendarEventId]/feedback"
            text={messages.commonFeedback}
          />
        }
      >
        {question && analytics ? (
          <CalendarEventFeedbackQuestionAnalytics
            analytics={analytics}
            question={question}
          />
        ) : null}
      </Box>

      {question ? (
        <div className="answers">
          <CalendarEventFeedbackQuestionAnswers
            filterId={filterId}
            filters={filters}
            numberOfEmptyAnswers={analytics?.numberOfEmptyAnswers}
            question={question}
          />
        </div>
      ) : null}

      <style jsx>{`
        .answers {
          margin-top: 25px;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventFeedbackQuestion;
